import { useEffect, useState, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { API } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import {
  ArrowLeft, Loader2, FileText, ListTodo, StickyNote, Users, Upload, Download,
  Eye, EyeOff, Plus, CheckCircle2, Clock, Circle,
} from "lucide-react";

const FULL = ["admin", "analista", "responsabile"];
const TASK_STATI = { aperta: { c: "#EA7B2B", i: Circle, l: "Aperta" }, in_corso: { c: "#60A5FA", i: Clock, l: "In corso" }, completata: { c: "#10B981", i: CheckCircle2, l: "Completata" } };

export default function DataRoomDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const full = FULL.includes(user?.role);
  const [d, setD] = useState(null);
  const [assignable, setAssignable] = useState([]);
  const fileRef = useRef();
  const [visInv, setVisInv] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [taskTitle, setTaskTitle] = useState("");
  const [taskAssignee, setTaskAssignee] = useState("");
  const [note, setNote] = useState("");

  const load = useCallback(() => {
    api.get(`/dataroom/${id}`).then((r) => setD(r.data)).catch((e) => {
      if (e.response?.status === 403) { toast.error("Non autorizzato"); nav("/dataroom"); }
    });
  }, [id, nav]);

  useEffect(() => {
    load();
    if (full) api.get("/dataroom/utenti-assegnabili").then((r) => setAssignable(r.data)).catch(() => {});
  }, [load, full]);

  const upload = async () => {
    const file = fileRef.current?.files?.[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("visibile_investitore", visInv);
    try {
      await api.post(`/dataroom/${id}/documenti`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Documento caricato");
      fileRef.current.value = "";
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setUploading(false); }
  };

  const addTask = async () => {
    if (!taskTitle.trim()) return;
    try {
      await api.post(`/dataroom/${id}/attivita`, { titolo: taskTitle, assegnato_a_id: taskAssignee || null });
      setTaskTitle(""); setTaskAssignee(""); load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const setTaskStato = async (tid, stato) => {
    try { await api.patch(`/dataroom/${id}/attivita/${tid}`, { stato }); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const addNote = async () => {
    if (!note.trim()) return;
    try { await api.post(`/dataroom/${id}/note`, { testo: note }); setNote(""); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const toggleAuth = async (uid) => {
    const cur = d.autorizzati.map((a) => a.id);
    const next = cur.includes(uid) ? cur.filter((x) => x !== uid) : [...cur, uid];
    try { await api.put(`/dataroom/${id}/autorizzati`, { user_ids: next }); toast.success("Autorizzazioni aggiornate"); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  if (!d) return <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-[#EA7B2B]" /></div>;

  return (
    <div className="space-y-6" data-testid="dataroom-detail">
      <button onClick={() => nav("/dataroom")} className="flex items-center gap-2 text-sm text-zinc-400 hover:text-white" data-testid="dataroom-back">
        <ArrowLeft className="w-4 h-4" /> Data Room
      </button>
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-light text-white">{d.comune} · {d.indirizzo}</h1>
          <p className="text-sm text-zinc-500 mt-1">{d.tipologia} · Stato: {d.stato} · Score {d.score} · ROI {d.roi != null ? `${d.roi}%` : "—"}</p>
        </div>
        {d.readonly && <span className="text-[10px] uppercase px-2 py-1 rounded-sm bg-white/5 text-zinc-400 border border-white/10">Sola lettura</span>}
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        {/* Documenti */}
        <Card title="Documenti condivisi" icon={FileText} testid="dr-documenti">
          {full && (
            <div className="flex items-center gap-2 mb-3 flex-wrap">
              <input ref={fileRef} type="file" data-testid="dr-file-input" className="text-xs text-zinc-400 file:mr-2 file:py-1.5 file:px-3 file:rounded-sm file:border-0 file:bg-white/10 file:text-white" />
              <label className="flex items-center gap-1.5 text-xs text-zinc-400 cursor-pointer">
                <input type="checkbox" checked={visInv} onChange={(e) => setVisInv(e.target.checked)} data-testid="dr-visible-investor" /> visibile investitore
              </label>
              <button onClick={upload} disabled={uploading} data-testid="dr-upload-btn" className="flex items-center gap-1.5 text-xs bg-[#EA7B2B] text-black px-3 py-1.5 rounded-sm hover:bg-[#F59E4A] disabled:opacity-50">
                {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />} Carica
              </button>
            </div>
          )}
          {d.documenti.length === 0 ? <p className="text-sm text-zinc-500">Nessun documento.</p> : (
            <div className="space-y-1.5" data-testid="dr-doc-list">
              {d.documenti.map((doc) => (
                <div key={doc.id} className="flex items-center justify-between gap-2 py-2 border-b border-white/5">
                  <div className="min-w-0">
                    <div className="text-sm text-white truncate">{doc.nome}</div>
                    <div className="text-[11px] text-zinc-500">{doc.caricato_da} · {new Date(doc.data).toLocaleDateString("it-IT")}</div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    {full && (doc.visibile_investitore ? <Eye className="w-3.5 h-3.5 text-[#10B981]" title="Visibile investitore" /> : <EyeOff className="w-3.5 h-3.5 text-zinc-600" title="Interno" />)}
                    <a href={`${API}/dataroom/${id}/documenti/${doc.id}/download`} target="_blank" rel="noreferrer" className="text-zinc-400 hover:text-[#EA7B2B]" data-testid={`dr-download-${doc.id}`}><Download className="w-4 h-4" /></a>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>

        {/* Attività */}
        <Card title="Attività / To-do" icon={ListTodo} testid="dr-attivita">
          {full && (
            <div className="flex items-center gap-2 mb-3">
              <input value={taskTitle} onChange={(e) => setTaskTitle(e.target.value)} placeholder="Nuova attività…" data-testid="dr-task-input" className="flex-1 bg-[#0A1826] border border-white/10 rounded-sm px-2.5 py-1.5 text-sm text-white focus:outline-none focus:ring-1 focus:ring-[#EA7B2B]" />
              <select value={taskAssignee} onChange={(e) => setTaskAssignee(e.target.value)} data-testid="dr-task-assignee" className="bg-[#0A1826] border border-white/10 rounded-sm px-2 py-1.5 text-xs text-white">
                <option value="">Assegna…</option>
                {assignable.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
              </select>
              <button onClick={addTask} data-testid="dr-task-add" className="bg-[#EA7B2B] text-black px-2.5 py-1.5 rounded-sm hover:bg-[#F59E4A]"><Plus className="w-4 h-4" /></button>
            </div>
          )}
          {d.attivita.length === 0 ? <p className="text-sm text-zinc-500">Nessuna attività.</p> : (
            <div className="space-y-1.5" data-testid="dr-task-list">
              {d.attivita.map((t) => {
                const s = TASK_STATI[t.stato] || TASK_STATI.aperta; const Icon = s.i;
                return (
                  <div key={t.id} className="flex items-center justify-between gap-2 py-2 border-b border-white/5" data-testid={`dr-task-${t.id}`}>
                    <div className="min-w-0">
                      <div className="text-sm text-white truncate">{t.titolo}</div>
                      <div className="text-[11px] text-zinc-500">{t.assegnato_a_nome ? `→ ${t.assegnato_a_nome}` : "non assegnata"}</div>
                    </div>
                    {full ? (
                      <select value={t.stato} onChange={(e) => setTaskStato(t.id, e.target.value)} data-testid={`dr-task-status-${t.id}`} className="bg-[#0A1826] border border-white/10 rounded-sm px-2 py-1 text-[11px]" style={{ color: s.c }}>
                        {Object.entries(TASK_STATI).map(([k, v]) => <option key={k} value={k}>{v.l}</option>)}
                      </select>
                    ) : <span className="inline-flex items-center gap-1 text-[11px]" style={{ color: s.c }}><Icon className="w-3.5 h-3.5" /> {s.l}</span>}
                  </div>
                );
              })}
            </div>
          )}
        </Card>

        {/* Note interne (solo team) */}
        {full && (
          <Card title="Note interne" icon={StickyNote} testid="dr-note">
            <div className="flex items-center gap-2 mb-3">
              <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Nota interna (non visibile agli investitori)…" data-testid="dr-note-input" className="flex-1 bg-[#0A1826] border border-white/10 rounded-sm px-2.5 py-1.5 text-sm text-white focus:outline-none focus:ring-1 focus:ring-[#EA7B2B]" />
              <button onClick={addNote} data-testid="dr-note-add" className="bg-[#EA7B2B] text-black px-2.5 py-1.5 rounded-sm hover:bg-[#F59E4A]"><Plus className="w-4 h-4" /></button>
            </div>
            {d.note.length === 0 ? <p className="text-sm text-zinc-500">Nessuna nota.</p> : (
              <div className="space-y-2">
                {d.note.map((n) => (
                  <div key={n.id} className="text-sm bg-[#0A1826] border border-white/5 rounded-sm p-2.5">
                    <div className="text-zinc-200">{n.testo}</div>
                    <div className="text-[11px] text-zinc-500 mt-1">{n.autore_nome} · {new Date(n.created_at).toLocaleString("it-IT")}</div>
                  </div>
                ))}
              </div>
            )}
          </Card>
        )}

        {/* Autorizzazioni (admin/responsabile) */}
        {["admin", "responsabile"].includes(user?.role) && (
          <Card title="Utenti autorizzati" icon={Users} testid="dr-auth">
            <p className="text-[12px] text-zinc-500 mb-3">Seleziona chi può accedere a questa operazione (gli investitori vedono solo i documenti condivisi).</p>
            <div className="space-y-1.5">
              {assignable.map((u) => {
                const on = d.autorizzati.some((a) => a.id === u.id);
                return (
                  <label key={u.id} className="flex items-center gap-2 text-sm cursor-pointer py-1" data-testid={`dr-auth-${u.id}`}>
                    <input type="checkbox" checked={on} onChange={() => toggleAuth(u.id)} />
                    <span className="text-white">{u.name}</span>
                    <span className="text-[11px] text-zinc-500">{u.role}</span>
                  </label>
                );
              })}
            </div>
          </Card>
        )}
      </div>
    </div>
  );
}

function Card({ title, icon: Icon, children, testid }) {
  return (
    <div className="bg-[#122A3F] border border-white/10 rounded-sm" data-testid={testid}>
      <div className="px-5 py-3 border-b border-white/10">
        <h2 className="text-sm font-medium text-zinc-200 uppercase tracking-wider flex items-center gap-2">{Icon && <Icon className="w-4 h-4 text-[#EA7B2B]" />} {title}</h2>
      </div>
      <div className="p-5">{children}</div>
    </div>
  );
}
